/**
 * The Figure Co. logo — recreated in code so it is resolution-independent and
 * fully theme-aware (letters inherit the theme's primary text color; the
 * signature double underline uses the brand accent). No raster assets.
 *
 * NOTE: the wordmark approximates the brand's condensed geometric face with
 * Outfit (the body font) at heavy weight — swap in the real face/vector when
 * available. The monogram is a geometric FC built from the brand's letterforms.
 */

import { BRAND_NAME, BRAND_TAGLINE } from '../../config/brand';

type Size = 'sm' | 'md' | 'lg';

const SIZE_PX: Record<Size, number> = { sm: 16, md: 20, lg: 26 };

interface WordmarkProps {
  size?: Size;
  /** Show the "Forensic BI" tagline line under the rule. */
  tagline?: boolean;
  className?: string;
}

/**
 * Horizontal lockup: THE FIGURE CO. · double orange rule · FORENSIC BI.
 * The rule stretches to the wordmark's width (flex-col cross-axis stretch).
 */
export function FigureCoWordmark({ size = 'md', tagline = true, className = '' }: WordmarkProps) {
  const fs = SIZE_PX[size];
  return (
    <span
      className={`inline-flex flex-col leading-none ${className}`}
      aria-label={tagline ? `${BRAND_NAME} — ${BRAND_TAGLINE}` : BRAND_NAME}
    >
      <span
        className="font-body font-extrabold uppercase text-text-primary"
        style={{ fontSize: fs, letterSpacing: '0.01em', lineHeight: 1 }}
      >
        {BRAND_NAME}
      </span>
      <span className="mt-[0.3em] flex flex-col gap-[0.12em]" aria-hidden="true">
        <span className="h-[2px] w-full bg-brand" />
        <span className="h-[2px] w-full bg-brand" />
      </span>
      {tagline && (
        <span
          className="mt-[0.35em] font-mono uppercase text-text-muted"
          style={{ fontSize: Math.max(8, Math.round(fs * 0.42)), letterSpacing: '0.34em' }}
        >
          {BRAND_TAGLINE}
        </span>
      )}
    </span>
  );
}

interface MonogramProps {
  /** Pixel size of the square mark. */
  size?: number;
  className?: string;
  /** Include the double underline rule beneath the FC. */
  rule?: boolean;
}

/**
 * FC monogram — geometric, cream (currentColor) letters with the brand-accent
 * double rule. Used for compact contexts (collapsed nav) and the favicon source.
 */
export function FigureCoMonogram({ size = 40, className = '', rule = true }: MonogramProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      role="img"
      aria-label={`${BRAND_NAME} monogram`}
      className={className}
    >
      {/* F */}
      <rect x="10" y="8" width="9" height="40" fill="currentColor" />
      <rect x="10" y="8" width="26" height="9" fill="currentColor" />
      <rect x="10" y="23" width="18" height="8" fill="currentColor" />
      {/* C (offset lower-right, interlocking with the F stem) */}
      <rect x="33" y="28" width="9" height="28" fill="currentColor" />
      <rect x="33" y="28" width="21" height="9" fill="currentColor" />
      <rect x="33" y="47" width="21" height="9" fill="currentColor" />
      {/* double rule */}
      {rule && (
        <>
          <rect x="16" y="59" width="32" height="2" fill="var(--color-brand)" />
          <rect x="16" y="62" width="32" height="1.5" fill="var(--color-brand)" />
        </>
      )}
    </svg>
  );
}

export default FigureCoWordmark;
